import { BUCKETS } from "../model/schedule.ts";
import { CRORE } from "../model/types.ts";
import type { BucketKey, SolveResult } from "../model/types.ts";

const LABELS: Record<BucketKey, string> = {
  life: "Lifestyle",
  school: "Schooling",
  undergrad: "Undergrad",
  elder: "Parents' care",
  health: "Health cover",
  access: "Access & travel",
};

const W = 880;
const H = 300;
const PAD = { l: 54, r: 16, t: 16, b: 32 };

/**
 * Annual outflow by bucket, deflated to retirement-year rupees so the
 * education and elder-care humps are visible rather than buried under
 * compounding.
 */
export function SpendChart({ result, startYear }: { result: SolveResult; startYear: number }) {
  const n = result.realOutflow.life.length;
  if (!n) return null;

  const totals = Array.from({ length: n }, (_, t) =>
    BUCKETS.reduce((sum, k) => sum + result.realOutflow[k][t], 0) / CRORE,
  );
  const peak = Math.max(...totals);
  const peakYear = totals.indexOf(peak);
  const yMax = Math.ceil(peak * 1.15 * 2) / 2 || 1;

  const iw = W - PAD.l - PAD.r;
  const ih = H - PAD.t - PAD.b;
  const x = (i: number) => PAD.l + (iw * i) / (n - 1);
  const y = (v: number) => PAD.t + ih * (1 - v / yMax);

  const cum = new Array(n).fill(0);
  const areas = BUCKETS.map((k) => {
    const top: string[] = [];
    const bottom: string[] = [];
    for (let i = 0; i < n; i++) {
      bottom.push(`${x(i).toFixed(1)},${y(cum[i]).toFixed(1)}`);
      cum[i] += result.realOutflow[k][i] / CRORE;
      top.push(`${x(i).toFixed(1)},${y(cum[i]).toFixed(1)}`);
    }
    return { key: k, points: `${top.join(" ")} ${bottom.reverse().join(" ")}` };
  });

  const gridStep = yMax > 4 ? 2 : yMax > 2 ? 1 : 0.5;
  const gridLines: number[] = [];
  for (let v = 0; v <= yMax + 1e-9; v += gridStep) gridLines.push(v);

  const xTicks = [0, Math.round((n - 1) / 4), Math.round((n - 1) / 2), Math.round(((n - 1) * 3) / 4), n - 1];

  return (
    <figure className="chart">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`Annual spending by category from year 1 to year ${n}, peaking at ${peak.toFixed(2)} crore around year ${peakYear + 1}.`}
      >
        {gridLines.map((v) => (
          <g key={v}>
            <line className="grid" x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} />
            <text className="tick" x={PAD.l - 9} y={y(v) + 3.5} textAnchor="end">
              {v.toFixed(1)}
            </text>
          </g>
        ))}
        <text className="tick" x={PAD.l - 9} y={PAD.t - 4} textAnchor="end">
          ₹cr
        </text>

        {areas.map((a) => (
          <polygon key={a.key} className={`area area-${a.key}`} points={a.points} />
        ))}

        <line className="annoline" x1={x(peakYear)} x2={x(peakYear)} y1={y(peak)} y2={PAD.t + 12} />
        <text className="anno" x={x(peakYear) + 7} y={PAD.t + 13}>
          peak ₹{peak.toFixed(1)} cr · {startYear + peakYear}
        </text>

        <line className="axis" x1={PAD.l} x2={W - PAD.r} y1={y(0)} y2={y(0)} />
        {xTicks.map((i, idx) => (
          <text
            key={i}
            className="tick"
            x={x(i)}
            y={H - 12}
            textAnchor={idx === 0 ? "start" : idx === xTicks.length - 1 ? "end" : "middle"}
          >
            {startYear + i}
          </text>
        ))}
      </svg>

      <div className="legend">
        {BUCKETS.map((k) => (
          <span key={k}>
            <i className={`swatch area-${k}`} />
            {LABELS[k]}
          </span>
        ))}
      </div>
      <figcaption>
        Deflated to {startYear} rupees at the general inflation rate, so each hump shows its real
        size. Lifestyle drifts up because a fat basket inflates faster than headline CPI.
      </figcaption>
    </figure>
  );
}
